import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

/**
 * FR-015-102, § Live regions: exactly one polite and one assertive region mounted once in
 * the shell; announcements throttled to one per 500 ms per region with bursts collapsed
 * into a count, so a busy stream does not flood a screen reader. The two regions are visually
 * hidden, so there is no Spectrum UI or shadcn/ui source to take (2026-09-18, slice (f)).
 */
export const ANNOUNCE_THROTTLE_MS = 500;

export type Politeness = "polite" | "assertive";

export interface Announcer {
  readonly announce: (message: string, politeness?: Politeness) => void;
}

const AnnouncerContext = createContext<Announcer | null>(null);

interface Channel {
  readonly text: string;
  readonly pendingCount: number;
  readonly lastAt: number;
}

/** The message collapsing a burst renders: the host supplies it from the catalogue. */
export interface LiveRegionProps {
  readonly collapsed: (count: number) => string;
  readonly children: ReactNode;
}

export function LiveRegion({ collapsed, children }: LiveRegionProps) {
  const [polite, setPolite] = useState("");
  const [assertive, setAssertive] = useState("");
  const channels = useRef<Record<Politeness, Channel>>({ polite: { text: "", pendingCount: 0, lastAt: 0 }, assertive: { text: "", pendingCount: 0, lastAt: 0 } });
  const timers = useRef<Record<Politeness, ReturnType<typeof setTimeout> | null>>({ polite: null, assertive: null });

  const flush = useCallback(
    (politeness: Politeness) => {
      const channel = channels.current[politeness];
      const text = channel.pendingCount > 1 ? collapsed(channel.pendingCount) : channel.text;
      channels.current[politeness] = { text: "", pendingCount: 0, lastAt: Date.now() };
      timers.current[politeness] = null;
      (politeness === "polite" ? setPolite : setAssertive)(text);
    },
    [collapsed],
  );

  const announce = useCallback(
    (message: string, politeness: Politeness = "polite") => {
      const channel = channels.current[politeness];
      const now = Date.now();
      channels.current[politeness] = { text: message, pendingCount: channel.pendingCount + 1, lastAt: channel.lastAt };
      if (timers.current[politeness] !== null) return;
      const wait = Math.max(0, ANNOUNCE_THROTTLE_MS - (now - channel.lastAt));
      timers.current[politeness] = setTimeout(() => {
        flush(politeness);
      }, wait);
    },
    [flush],
  );

  useEffect(() => {
    const current = timers.current;
    return () => {
      for (const timer of Object.values(current)) if (timer) clearTimeout(timer);
    };
  }, []);

  const value = useMemo<Announcer>(() => ({ announce }), [announce]);
  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      <div className="sr-only" aria-live="polite" aria-atomic="true" data-live-region="polite">
        {polite}
      </div>
      <div className="sr-only" aria-live="assertive" aria-atomic="true" data-live-region="assertive">
        {assertive}
      </div>
    </AnnouncerContext.Provider>
  );
}

export function useAnnouncer(): Announcer {
  const value = useContext(AnnouncerContext);
  if (value === null) throw new Error("useAnnouncer must be used inside <LiveRegion>");
  return value;
}
